import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);
const EMPTY_CART = { items: [], truckId: null, truckName: null };

export function CartProvider({ children }) {
  const { user } = useAuth();
  const cartStorageKey = `skipdq_cart_${user?.user_id || 'guest'}`;
  const legacyCartStorageKey = `trucktime_cart_${user?.user_id || 'guest'}`;
  const [cart, setCart] = useState(EMPTY_CART);

  useEffect(() => {
    const saved =
      localStorage.getItem(cartStorageKey) || localStorage.getItem(legacyCartStorageKey);
    setCart(saved ? JSON.parse(saved) : EMPTY_CART);
    if (saved && !localStorage.getItem(cartStorageKey)) {
      localStorage.setItem(cartStorageKey, saved);
      localStorage.removeItem(legacyCartStorageKey);
    }
  }, [cartStorageKey, legacyCartStorageKey]);

  useEffect(() => {
    localStorage.setItem(cartStorageKey, JSON.stringify(cart));
  }, [cart, cartStorageKey]);

  const addItem = (item, truck) => {
    setCart(prev => {
      // If cart has items from different truck, clear it
      if (prev.truckId && prev.truckId !== truck.truck_id) {
        return {
          items: [{ ...item, quantity: 1 }],
          truckId: truck.truck_id,
          truckName: truck.name
        };
      }

      const existingIndex = prev.items.findIndex(i => i.item_id === item.item_id);
      if (existingIndex >= 0) {
        const newItems = [...prev.items];
        newItems[existingIndex].quantity += 1;
        return { ...prev, items: newItems };
      }

      return {
        items: [...prev.items, { ...item, quantity: 1 }],
        truckId: truck.truck_id,
        truckName: truck.name
      };
    });
  };

  const updateQuantity = (itemId, quantity) => {
    setCart(prev => {
      if (quantity <= 0) {
        const newItems = prev.items.filter(i => i.item_id !== itemId);
        if (newItems.length === 0) {
          return EMPTY_CART;
        }
        return { ...prev, items: newItems };
      }

      const newItems = prev.items.map(i => 
        i.item_id === itemId ? { ...i, quantity } : i
      );
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (itemId) => {
    setCart(prev => {
      const newItems = prev.items.filter(i => i.item_id !== itemId);
      if (newItems.length === 0) {
        return EMPTY_CART;
      }
      return { ...prev, items: newItems };
    });
  };

  const clearCart = () => {
    setCart(EMPTY_CART);
  };

  const getTotal = () => {
    return cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getItemCount = () => {
    return cart.items.reduce((sum, item) => sum + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{
      cart,
      addItem,
      updateQuantity,
      removeItem,
      clearCart,
      getTotal,
      getItemCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
}
